import { normalizeStoredFormSchema } from './form-io.util';
import { visitNestedColumns, visitNestedComponents } from './form-io-traverse.util';
import { EXCLUDED_SUBMISSION_DATA_KEYS } from './form-submission-constants.util';

const isSignatureNode = (node) =>
  node?.type === 'signature' &&
  typeof node.key === 'string' &&
  node.key.trim() !== '';

const addNodeKeyIfSignature = (node, keys) => {
  if (isSignatureNode(node)) {
    keys.add(node.key.trim());
  }
};

const collectSignatureKeysFromNode = (node, keys) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addNodeKeyIfSignature(node, keys);
  const visit = (child) => collectSignatureKeysFromNode(child, keys);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Collect `key`s for Form.io `signature` components from a stored form definition.
 * @param {unknown} definition
 * @returns {ReadonlySet<string>}
 */
export const extractSignatureKeysFromFormDefinition = (definition) => {
  /** @type {Set<string>} */
  const keys = new Set();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectSignatureKeysFromNode(child, keys));
  return keys;
};

const isCurrencyNode = (node) =>
  node?.type === 'currency' && typeof node.key === 'string' && node.key.trim() !== '';

const addCurrencyMetaFromNode = (node, map) => {
  if (!isCurrencyNode(node)) {
    return;
  }
  const key = node.key.trim();
  const code =
    typeof node.currency === 'string' && node.currency.trim() !== ''
      ? node.currency.trim()
      : 'USD';
  map.set(key, code);
};

const collectCurrencyMetaFromNode = (node, map) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addCurrencyMetaFromNode(node, map);
  const visit = (child) => collectCurrencyMetaFromNode(child, map);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Map Form.io field key → ISO currency code for `currency` components (default USD).
 * @param {unknown} definition API form definition with `schema`.
 * @returns {ReadonlyMap<string, string>}
 */
export const collectCurrencyMetaFromFormDefinition = (definition) => {
  const map = new Map();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectCurrencyMetaFromNode(child, map));
  return map;
};

const isSelectboxesNode = (node) =>
  node?.type === 'selectboxes' && typeof node.key === 'string' && node.key.trim() !== '';

const addSelectboxKeyIfSelectboxes = (node, keys) => {
  if (isSelectboxesNode(node)) {
    keys.add(node.key.trim());
  }
};

const collectSelectboxKeysFromNode = (node, keys) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addSelectboxKeyIfSelectboxes(node, keys);
  const visit = (child) => collectSelectboxKeysFromNode(child, keys);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Collect `key`s for Form.io `selectboxes` components from a stored form definition.
 * @param {unknown} definition
 * @returns {ReadonlySet<string>}
 */
export const extractSelectboxKeysFromFormDefinition = (definition) => {
  /** @type {Set<string>} */
  const keys = new Set();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectSelectboxKeysFromNode(child, keys));
  return keys;
};

const isSurveyNode = (node) =>
  node?.type === 'survey' && typeof node.key === 'string' && node.key.trim() !== '';

const addSurveyKeyIfSurvey = (node, keys) => {
  if (isSurveyNode(node)) {
    keys.add(node.key.trim());
  }
};

const collectSurveyKeysFromNode = (node, keys) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addSurveyKeyIfSurvey(node, keys);
  const visit = (child) => collectSurveyKeysFromNode(child, keys);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Collect `key`s for Form.io `survey` components from a stored form definition.
 * @param {unknown} definition
 * @returns {ReadonlySet<string>}
 */
export const extractSurveyKeysFromFormDefinition = (definition) => {
  /** @type {Set<string>} */
  const keys = new Set();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectSurveyKeysFromNode(child, keys));
  return keys;
};

const isDatetimeNode = (node) =>
  node?.type === 'datetime' && typeof node.key === 'string' && node.key.trim() !== '';

const addDatetimeKeyIfDatetime = (node, keys) => {
  if (isDatetimeNode(node)) {
    keys.add(node.key.trim());
  }
};

const collectDatetimeKeysFromNode = (node, keys) => {
  if (!node || typeof node !== 'object') {
    return;
  }
  addDatetimeKeyIfDatetime(node, keys);
  const visit = (child) => collectDatetimeKeysFromNode(child, keys);
  visitNestedComponents(node, visit);
  visitNestedColumns(node, visit);
};

/**
 * Collect `key`s for Form.io `datetime` components from a stored form definition.
 * @param {unknown} definition
 * @returns {ReadonlySet<string>}
 */
export const extractDatetimeKeysFromFormDefinition = (definition) => {
  /** @type {Set<string>} */
  const keys = new Set();
  const form = normalizeStoredFormSchema(definition?.schema);
  visitNestedComponents(form, (child) => collectDatetimeKeysFromNode(child, keys));
  return keys;
};

/**
 * Sorted union of keys from `submission.data` objects across rows (Form.io field keys).
 * @param {readonly { data?: unknown }[]} submissions
 * @returns {string[]}
 */
const addRowKeysToSet = (row, keys) => {
  const raw = row?.data;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return;
  }
  Object.keys(raw).forEach((k) => {
    if (!EXCLUDED_SUBMISSION_DATA_KEYS.has(k.toLowerCase())) {
      keys.add(k);
    }
  });
};

export const collectSortedDataKeysFromSubmissions = (submissions) => {
  const keys = new Set();
  if (!Array.isArray(submissions)) {
    return [];
  }
  submissions.forEach((row) => addRowKeysToSet(row, keys));
  return [...keys].sort((a, b) => a.localeCompare(b));
};

/**
 * Field metadata derived from a form definition (signature, currency, selectbox, survey, datetime).
 * @param {unknown} formDefinition
 */
export const collectSubmissionFieldMetaFromDefinition = (formDefinition) => ({
  signatureKeys: extractSignatureKeysFromFormDefinition(formDefinition),
  currencyMeta: collectCurrencyMetaFromFormDefinition(formDefinition),
  selectboxKeys: extractSelectboxKeysFromFormDefinition(formDefinition),
  surveyKeys: extractSurveyKeysFromFormDefinition(formDefinition),
  datetimeKeys: extractDatetimeKeysFromFormDefinition(formDefinition),
});
